import { type Instant, plusSeconds } from '@taler/domain'
import type { OperationBody, OperationResult } from '../contract/types.ts'
import type { AccessToken } from '../http/auth.ts'
import type { TalerClient } from '../http/client.ts'

export const AUTH_BASE = '/api/v1/auth'

export type ExchangeRequest = OperationBody<'auth_exchange'>
export type SessionTokenResponse = OperationResult<'auth_exchange'>

export interface AuthRepository {
  /** Trades an authorization code for a session. The only call that carries a verifier. */
  exchange(request: ExchangeRequest, signal?: AbortSignal): Promise<AccessToken>
  /** Renews without user interaction, on the renewal credential the caller holds. */
  refresh(refreshToken?: string, signal?: AbortSignal): Promise<AccessToken>
  /** Revokes the session at the backend so another tab, and the next visit, are signed out. */
  signOut(): Promise<void>
}

export interface CreateAuthRepositoryOptions {
  /**
   * A client built with `anonymousAuth`. These calls carry no bearer token and must not
   * trigger renewal on a 401 - that is the recursion this separation exists to prevent.
   */
  readonly client: TalerClient
  readonly now: () => Instant
}

export function createAuthRepository(options: CreateAuthRepositoryOptions): AuthRepository {
  const toToken = (response: SessionTokenResponse): AccessToken => ({
    value: response.accessToken,
    expiresAt: plusSeconds(options.now(), response.expiresInSeconds),
  })

  return {
    async exchange(request, signal) {
      return toToken(
        await options.client.call<SessionTokenResponse, ExchangeRequest>({
          method: 'POST',
          path: `${AUTH_BASE}/exchange`,
          body: request,
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async refresh(refreshToken, signal) {
      const body: OperationBody<'auth_refresh'> = refreshToken === undefined ? {} : { refreshToken }
      const path = `${AUTH_BASE}/refresh`
      try {
        return toToken(
          await options.client.call<SessionTokenResponse, OperationBody<'auth_refresh'>>({
            method: 'POST',
            path,
            body,
            ...(signal === undefined ? {} : { signal }),
          }),
        )
      } finally {
        // Each renewal is a new act; a reused key would replay the first, long-expired token.
        options.client.idempotencyKeys.forget({ method: 'POST', path, query: null, body })
      }
    },

    async signOut() {
      await options.client.call<void, Record<string, never>>({
        method: 'POST',
        path: `${AUTH_BASE}/sign-out`,
        body: {},
      })
    },
  }
}
